import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Card } from '@/components/ui'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import {
  RewardDefinitionDto,
  RewardPoolDto,
  RewardRuleDto,
} from '@/common/models/gamification'
import {
  getRewardDefinition,
  getRewardPools,
  getRewardRules,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { translationTitle } from './shared/labels'
import { RewardRuleList } from './rewards/RewardRuleList'

/** Which of the three distribution modes the reward is in — see `RewardDefinitionDto`. */
const distributionLabel = (definition: RewardDefinitionDto): string => {
  if (definition.displayOnly) return 'Display only'
  return definition.pointPrice != null ? 'Claimable' : 'Auto-grant'
}

export const RewardDefinitionDetailPage: React.FC = () => {
  const { festivalName, editionId, defId } = useParams()
  const navigate = useNavigate()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [definition, setDefinition] = useState<RewardDefinitionDto | null>(
    null,
  )
  const [rules, setRules] = useState<RewardRuleDto[]>([])
  const [pools, setPools] = useState<RewardPoolDto[]>([])
  const [loading, setLoading] = useState(false)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = useCallback((e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }, [])

  const refetch = useCallback(async () => {
    if (!defId) return
    try {
      setLoading(true)
      const { data } = await getRewardRules(defId)
      setRules(data ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [defId, handleError])

  const loadAll = useCallback(async () => {
    if (!defId) return
    try {
      setLoading(true)
      const [definitionRes, rulesRes, poolsRes] = await Promise.all([
        getRewardDefinition(defId),
        getRewardRules(defId),
        getRewardPools(),
      ])
      setDefinition(definitionRes.data)
      setRules(rulesRes.data ?? [])
      setPools(poolsRes.data ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [defId, handleError])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const title = definition
    ? translationTitle(definition.translations, `${definition.typeKey} reward`)
    : 'Reward'

  return (
    <>
      <Header pageTitle="Gamification" />
      {loading && <Loader />}

      <PageTitle
        icon="gift"
        title={title}
        subtitle={definition ? definition.typeKey : undefined}
        backButton
        backButtonFunction={() =>
          navigate(`/gamification/rewards/${festivalName}/${editionId}`)
        }
        className="mb-6"
      />

      {definition && (
        <div className="flex flex-col gap-6">
          <Card className="p-5 !bg-monochrome-4-60">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-xs text-monochrome-2">Type</div>
                <div className="text-monochrome-1">{definition.typeKey}</div>
              </div>
              <div>
                <div className="text-xs text-monochrome-2">Partner</div>
                <div className="text-monochrome-1">
                  {definition.partnerId || '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-monochrome-2">Point Price</div>
                <div className="text-monochrome-1">
                  {definition.pointPrice ?? '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-monochrome-2">Distribution</div>
                <div className="text-monochrome-1">
                  {distributionLabel(definition)}
                </div>
              </div>
            </div>
          </Card>

          <RewardRuleList
            defId={definition.id}
            rules={rules}
            pools={pools}
            hasEditPermission={hasEditPermission}
            displayOnly={definition.displayOnly}
            onChanged={refetch}
            onError={handleError}
          />
        </div>
      )}

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
